import { useEffect, useSyncExternalStore } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { readThisWeek } from '@actual-app/core/shared/mtxbudget';
import type { AccountEntity } from '@actual-app/core/types/models';

/** The day each bank's data stands at, as the server noted it at each read. */
export type BankReads = {
  accounts: Record<
    string,
    {
      org: string;
      dataDate: string;
      needsAttention: boolean;
      /** What the bank says is available, in cents, as it sends it. */
      available?: number;
    }
  >;
};

// Kept for the whole session and shared by every screen, read again after
// every bank sync. Null while the server has not answered.
let reads: BankReads | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

type Ask = () => Promise<{ data: unknown } | { error: string }>;

/** Asks the server again; kept as it was when the server does not answer. */
export async function refreshBankReads(
  ask: Ask = () => send('mtxbudget-server', { route: 'banks' }),
): Promise<void> {
  loaded = true;
  const result = await ask();
  if ('data' in result) {
    reads = result.data as BankReads;
    listeners.forEach(listener => listener());
  }
}

export function useBankReads(): BankReads | null {
  useEffect(() => {
    if (!loaded) {
      void refreshBankReads();
    }
  }, []);
  return useSyncExternalStore(subscribe, () => reads);
}

/**
 * Whether the account's bank has sent data since Sunday: one good read a week
 * is enough. The day is the bank's own; before the server has noted a read,
 * the time of Actual's own read stands in for it.
 */
export function readThisWeekByBank(
  account: Pick<AccountEntity, 'account_id' | 'last_sync'>,
  today: string = monthUtils.currentDay(),
): boolean {
  return readThisWeek(bankDataDate(account), today);
}

/** The day the account's bank data stands at, as well as it is known. */
export function bankDataDate(
  account: Pick<AccountEntity, 'account_id' | 'last_sync'>,
): string | null {
  return (
    reads?.accounts[account.account_id ?? '']?.dataDate ??
    (account.last_sync
      ? monthUtils.dayFromDate(new Date(Number(account.last_sync)))
      : null)
  );
}

/** What the account's bank last said was available, in cents; null if unknown. */
export function bankAvailable(
  account: Pick<AccountEntity, 'account_id'>,
): number | null {
  return reads?.accounts[account.account_id ?? '']?.available ?? null;
}
